"use client"
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import { isPricingPlanAvailable } from 'app/lib/centralizedPricingPlans';
import { usePricingCms } from '@/app/contexts/PricingCmsContext';
import { cmsArrayFromKeyedMap } from '@/lib/cms/normalize-cms-payload';
import FaqAccordionItem from '@/app/components/faq/FaqAccordionItem';


const PricingFAQ: React.FunctionComponent = () => {
    const t = useTranslations("Pricing.faqSection");
    const cms = usePricingCms();
    const cmsFaqs = cmsArrayFromKeyedMap<{ question: string; answer: string }>(
        cms?.pricingFaqs,
    );
    const [isHydrated, setIsHydrated] = useState(false);
    const [openFaqKey, setOpenFaqKey] = useState<string | null>(null);

    const toggleFaq = (key: string) => {
        setOpenFaqKey((current) => (current === key ? null : key));
    };

    // Ensure translations are loaded before rendering to prevent hydration mismatch
    useEffect(() => {
        setIsHydrated(true);
    }, []);

    // Helper function to get translation with fallback
    const getTranslation = (key: string, fallback: string) => {
        if (!isHydrated) {
            return fallback;
        }
        const translated = t(key);
        if (
            translated === `Pricing.faqSection.${key}` ||
            translated.startsWith("Pricing.faqSection.")
        ) {
            return fallback;
        }
        return translated;
    };

    type Answers = {
        answerKey: string;
        answerFallback: string;
    }

    type FAQItem = {
        key: string;
        questionKey: string;
        questionFallback: string;
        answers: Answers[];
    }

    const faqItemsDefault: FAQItem[] = [
        {
            key: "creditsAndReset",
            questionKey: "creditsAndReset.question",
            questionFallback: "What is a credit and how is it used?",
            answers: [
                {
                    answerKey: "creditsAndReset.answer",
                    answerFallback:
                        "In PineGen AI, credits are monthly usage units in your plan that are used when you work with the platform’s AI features, such as generating, refining, explaining Pine Script code. The number of credits you get each month depends on your plan.",
                },
            ],
        },
        {
            key: "freeTierAvailability",
            questionKey: "freeTierAvailability.question",
            questionFallback: "Is PineGen AI free to use?",
            answers: [
                {
                    answerKey: "freeTierAvailability.answer1",
                    answerFallback: "Yes. PineGen AI has a free plan called Basic.",
                },
                {
                    answerKey: "freeTierAvailability.answer2",
                    answerFallback:
                        "The free plan is meant for users getting started with AI Pine Script generation using PineGen AI. It includes 5 credits per month, 1 chat thread, prompt builder, credit history, full backtesting and preview, and limited low-priority support.",
                },
                {
                    answerKey: "freeTierAvailability.answer3",
                    answerFallback:
                        "PineGen AI is free to use, but the free plan comes with lower limits compared to paid plans like Pro, Business, and Enterprise.",
                },
            ],
        },
        {
            key: "starterPlanDetails",
            questionKey: "starterPlanDetails.question",
            questionFallback:
                "What is the main difference between the Free and $39 (Pro) plan?",
            answers: [
                {
                    answerKey: "starterPlanDetails.answer1",
                    answerFallback:
                        "The Free plan is designed for beginners who are just getting started. It includes 5 credits per month and only 1 chat thread, with basic features like prompt builder, backtesting, live preview chart and limited support. It’s mainly useful for trying out the platform.",
                },
                {
                    answerKey: "starterPlanDetails.answer2",
                    answerFallback:
                        "The Pro plan ($39/month) is built for active users who want to use PineGen AI more consistently. It increases usage to 50 credits per month and 5 chat threads, and adds important features like code explanation and debugging, priority support, thread management, and the ability to buy extra credits.",
                },
                {
                    answerKey: "starterPlanDetails.answer3",
                    answerFallback:
                        "Free is for testing and learning, while Pro is for regular strategy building with more flexibility, higher limits, and better support.",
                },
            ],
        },
        {
            key: "differenceBetweenStarterAndBusinessPlan",
            questionKey: "differenceBetweenStarterAndBusinessPlan.question",
            questionFallback:
                "What is the main difference between the $39 (Pro) and $99 (Business) plans?",
            answers: [
                {
                    answerKey: "differenceBetweenStarterAndBusinessPlan.answer1",
                    answerFallback:
                        "The Pro plan is designed for individual traders and developers who want to go beyond the free tier. It includes 50 credits per month, allowing for a moderate level of strategy generation and testing. You also get 5 chat threads, along with features like full backtesting and preview, code explanation and debugging, thread delete feature, buy extra credits and priority support. This plan is ideal if you are actively building strategies but at a controlled pace and mainly working on your own.",
                },
                {
                    answerKey: "differenceBetweenStarterAndBusinessPlan.answer2",
                    answerFallback:
                        "The Business plan is built for higher usage and more demanding workflows. It increases capacity to 200 credits per month and expands to 20 chat threads, allowing you to run more strategies and manage more conversations at once. In addition to everything in Pro, it offers premium support, making it more suitable for users who rely on the platform more frequently. This plan is best for traders who need more volume, more threads, and smoother ongoing usage compared to Pro.",
                },
            ],
        },
        {
            key: "differenceBetweenBusinessAndEnterprisePlan",
            questionKey: "differenceBetweenBusinessAndEnterprisePlan.question",
            questionFallback:
                "What is the difference between the $99 (Business) and $999 (Enterprise) plans?",
            answers: [
                {
                    answerKey: "differenceBetweenBusinessAndEnterprisePlan.answer1",
                    answerFallback:
                        "The Business plan ($99/month) is designed for professionals and small teams who need higher usage. It includes 200 credits per month, 20 chat threads, full backtesting, code explanation, thread delete feature, ability to buy extra credits and premium support. It’s suitable for users who are actively generating and refining strategies at a higher volume.",
                },
                {
                    answerKey: "differenceBetweenBusinessAndEnterprisePlan.answer2",
                    answerFallback:
                        "The Enterprise plan ($999/month) is built for large teams and high-scale operations. It includes everything in Business, but significantly expands capacity to 2500 credits per month and provides unlimited chat threads. It also offers the highest level of priority support, along with faster and more accurate AI generations, full API access, prioritized processing during peak times, multi-variant strategy generation, team access and advanced optimization features.",
                },
            ],
        },
        {
            key: "planUpgradeProcess",
            questionKey: "planUpgradeProcess.question",
            questionFallback: "What happens if I run out of credits?",
            answers: [
                {
                    answerKey: "planUpgradeProcess.answer1",
                    answerFallback:
                        "If you run out of credits, you won’t be able to generate new strategies until you have credits available again.",
                },
                {
                    answerKey: "planUpgradeProcess.answer2",
                    answerFallback:
                        "You can either wait for your credits to reset at the start of your next billing cycle, upgrade your plan if you using the free plan with limited generations or purchase additional credits (available on paid plans) to continue using the platform without interruption.",
                },
            ],
        },
        {
            key: "usageTracking",
            questionKey: "usageTracking.question",
            questionFallback: "How to view credit history?",
            answers: [
                {
                    answerKey: "usageTracking.answer",
                    answerFallback:
                        "To view your credit history, open the app and check the Credit Usage section near the prompt box. You can also view it by going to the app, clicking your profile icon, open Settings, and then select Credit Usage.",
                },
            ],
        },
        {
            key: "enterprisePlanSuitability",
            questionKey: "enterprisePlanSuitability.question",
            questionFallback: "Who is the Enterprise plan for?",
            answers: [
                {
                    answerKey: "enterprisePlanSuitability.answer",
                    answerFallback:
                        "The Enterprise plan is for teams or high-volume users who need much higher limits and advanced capabilities. It is best suited for businesses that need 2500 credits per month, unlimited chat threads, team workspaces, full API access, faster AI generations, and advanced features built for larger-scale use.",
                },
            ],
        },
        {
            key: "promptRefinement",
            questionKey: "promptRefinement.question",
            questionFallback: "What is prompt builder?",
            answers: [
                {
                    answerKey: "promptRefinement.answer",
                    answerFallback:
                        "Prompt Builder is a feature that helps you create better prompts for strategy generation. Instead of writing everything manually, it guides you in structuring your request more clearly by helping you define things like entry conditions, exit conditions, indicators, filters, and risk management. This makes it easier to describe the strategy you want, especially if you are not sure how to phrase it properly.",
                },
            ],
        },
        {
            key: "advancedBacktesting",
            questionKey: "advancedBacktesting.question",
            questionFallback:
                "Does PineGen AI generate fastest code in Enterprise Plan?",
            answers: [
                {
                    answerKey: "advancedBacktesting.answer1",
                    answerFallback:
                        "All PineGen AI plans are capable of generating high-quality Pine Script code. However, the Enterprise plan offers the fastest and most prioritized generation.",
                },
                {
                    answerKey: "advancedBacktesting.answer2",
                    answerFallback:
                        "While Free, Pro, and Business plans also generate code efficiently, Enterprise users get top priority during peak times, along with faster processing and more advanced generation performance.",
                },
            ],
        },
        {
            key: "multiVariantGeneration",
            questionKey: "multiVariantGeneration.question",
            questionFallback:
                "What does “multi-variant strategy generation” mean?",
            answers: [
                {
                    answerKey: "multiVariantGeneration.answer",
                    answerFallback:
                        "It means you can generate multiple versions of the same strategy idea, so you can compare different variations of the logic instead of working with just one output. This feature is only available on the Enterprise plan, and it can make it easier to test alternatives and choose the version that fits your goal better.",
                },
            ],
        },
        {
            key: "aiAnalysis",
            questionKey: "aiAnalysis.question",
            questionFallback:
                "How does the Advanced AI Model improve strategy generation?",
            answers: [
                {
                    answerKey: "aiAnalysis.answer",
                    answerFallback:
                        "This feature is only available on the Enterprise plan. It uses a stronger AI system to understand your strategy idea more accurately and generate cleaner, more reliable Pine Script code, which can help reduce fixes and improve output quality.",
                },
            ],
        },
        {
            key: "priorityProcessing",
            questionKey: "priorityProcessing.question",
            questionFallback:
                "What is Priority Compute and how does it help during peak hours?",
            answers: [
                {
                    answerKey: "priorityProcessing.answer",
                    answerFallback:
                        "This feature is only available on the Enterprise plan. It gives your strategy generations priority when demand is high, so your requests can be processed faster during peak times. It helps reduce waiting time when many users are generating at the same time.",
                },
            ],
        },
        {
            key: "apiAccess",
            questionKey: "apiAccess.question",
            questionFallback: "What can I do with Full API Access?",
            answers: [
                {
                    answerKey: "apiAccess.answer",
                    answerFallback:
                        "You can connect PineGen AI to your own apps or systems and automate strategy generation using the full access API.",
                },
            ],
        },
        {
            key: "strategyDiscovery",
            questionKey: "strategyDiscovery.question",
            questionFallback:
                "What does Real-Time Strategy Discovery mean?",
            answers: [
                {
                    answerKey: "strategyDiscovery.answer",
                    answerFallback:
                        "It helps generate strategies using current market conditions instead of relying only on static inputs. This feature is only available on the Enterprise plan, and it is designed to make strategy generation more relevant to what is happening in the market right now.",
                },
            ],
        },
        {
            key: "backtestSpeed",
            questionKey: "backtestSpeed.question",
            questionFallback:
                "How does Multi-Source Intelligence improve strategy quality?",
            answers: [
                {
                    answerKey: "backtestSpeed.answer1",
                    answerFallback:
                        "It improves strategy quality by building strategy generations from multiple sources instead of relying on just one input, which can help produce more informed and better-rounded outputs.",
                },
                {
                    answerKey: "backtestSpeed.answer2",
                    answerFallback:
                        "This feature is only available on the Enterprise plan and it also sits alongside Enterprise features like strategy generations based on live markets, so the goal is to make the generated strategy more relevant and stronger overall.",
                },
            ],
        },
        {
            key: "planDowngrade",
            questionKey: "planDowngrade.question",
            questionFallback: "Can I cancel my plan anytime?",
            answers: [
                {
                    answerKey: "planDowngrade.answer",
                    answerFallback:
                        "Yes, you can cancel your subscription at any time. Once you cancel, your plan will remain active until the end of your current billing period, and you won’t be charged again after that.",
                },
            ],
        },
    ];

    const faqItemsWithoutEnterprise: FAQItem[] = [
        {
            key: "creditsAndReset",
            questionKey: "creditsAndReset.question",
            questionFallback: "What is a credit and how is it used?",
            answers: [
                {
                    answerKey: "creditsAndReset.answer",
                    answerFallback:
                        "In PineGen AI, credits are monthly usage units in your plan that are used when you work with the platform’s AI features, such as generating, refining, explaining Pine Script code. The number of credits you get each month depends on your plan.",
                },
            ],
        },
        {
            key: "freeTierAvailability",
            questionKey: "freeTierAvailability.question",
            questionFallback: "Is PineGen AI free to use?",
            answers: [
                {
                    answerKey: "freeTierAvailability.answer1",
                    answerFallback: "Yes. PineGen AI has a free plan called Basic.",
                },
                {
                    answerKey: "freeTierAvailability.answer2",
                    answerFallback:
                        "The free plan is meant for users getting started with AI Pine Script generation using PineGen AI. It includes 5 credits per month, 1 chat thread, prompt builder, credit history, full backtesting and preview, and limited low-priority support.",
                },
                {
                    answerKey: "freeTierAvailability.answerWithoutEnterprise",
                    answerFallback:
                        "PineGen AI is free to use, but the free plan comes with lower limits compared to paid plans like Pro, and Business.",
                },
            ],
        },
        {
            key: "starterPlanDetails",
            questionKey: "starterPlanDetails.question",
            questionFallback:
                "What is the main difference between the Free and $39 (Pro) plan?",
            answers: [
                {
                    answerKey: "starterPlanDetails.answer1",
                    answerFallback:
                        "The Free plan is designed for beginners who are just getting started. It includes 5 credits per month and only 1 chat thread, with basic features like prompt builder, backtesting, live preview chart and limited support. It’s mainly useful for trying out the platform.",
                },
                {
                    answerKey: "starterPlanDetails.answer2",
                    answerFallback:
                        "The Pro plan ($39/month) is built for active users who want to use PineGen AI more consistently. It increases usage to 50 credits per month and 5 chat threads, and adds important features like code explanation and debugging, priority support, thread management, and the ability to buy extra credits.",
                },
                {
                    answerKey: "starterPlanDetails.answer3",
                    answerFallback:
                        "Free is for testing and learning, while Pro is for regular strategy building with more flexibility, higher limits, and better support.",
                },
            ],
        },
        {
            key: "differenceBetweenStarterAndBusinessPlan",
            questionKey: "differenceBetweenStarterAndBusinessPlan.question",
            questionFallback:
                "What is the main difference between the $39 (Pro) and $99 (Business) plans?",
            answers: [
                {
                    answerKey: "differenceBetweenStarterAndBusinessPlan.answer1",
                    answerFallback:
                        "The Pro plan is designed for individual traders and developers who want to go beyond the free tier. It includes 50 credits per month, allowing for a moderate level of strategy generation and testing. You also get 5 chat threads, along with features like full backtesting and preview, code explanation and debugging, thread delete feature, buy extra credits and priority support. This plan is ideal if you are actively building strategies but at a controlled pace and mainly working on your own.",
                },
                {
                    answerKey: "differenceBetweenStarterAndBusinessPlan.answer2",
                    answerFallback:
                        "The Business plan is built for higher usage and more demanding workflows. It increases capacity to 200 credits per month and expands to 20 chat threads, allowing you to run more strategies and manage more conversations at once. In addition to everything in Pro, it offers premium support, making it more suitable for users who rely on the platform more frequently. This plan is best for traders who need more volume, more threads, and smoother ongoing usage compared to Pro.",
                },
            ],
        },
        {
            key: "planUpgradeProcess",
            questionKey: "planUpgradeProcess.question",
            questionFallback: "What happens if I run out of credits?",
            answers: [
                {
                    answerKey: "planUpgradeProcess.answer1",
                    answerFallback:
                        "If you run out of credits, you won’t be able to generate new strategies until you have credits available again.",
                },
                {
                    answerKey: "planUpgradeProcess.answer2",
                    answerFallback:
                        "You can either wait for your credits to reset at the start of your next billing cycle, upgrade your plan if you using the free plan with limited generations or purchase additional credits (available on paid plans) to continue using the platform without interruption.",
                },
            ],
        },
        {
            key: "usageTracking",
            questionKey: "usageTracking.question",
            questionFallback: "How to view credit history?",
            answers: [
                {
                    answerKey: "usageTracking.answer",
                    answerFallback:
                        "To view your credit history, open the app and check the Credit Usage section near the prompt box. You can also view it by going to the app, clicking your profile icon, open Settings, and then select Credit Usage.",
                },
            ],
        },
        {
            key: "promptRefinement",
            questionKey: "promptRefinement.question",
            questionFallback: "What is prompt builder?",
            answers: [
                {
                    answerKey: "promptRefinement.answer",
                    answerFallback:
                        "Prompt Builder is a feature that helps you create better prompts for strategy generation. Instead of writing everything manually, it guides you in structuring your request more clearly by helping you define things like entry conditions, exit conditions, indicators, filters, and risk management. This makes it easier to describe the strategy you want, especially if you are not sure how to phrase it properly.",
                },
            ],
        },
        {
            key: "apiAccess",
            questionKey: "apiAccess.question",
            questionFallback: "What can I do with Full API Access?",
            answers: [
                {
                    answerKey: "apiAccess.answer",
                    answerFallback:
                        "You can connect PineGen AI to your own apps or systems and automate strategy generation using the full access API.",
                },
            ],
        },
        {
            key: "planDowngrade",
            questionKey: "planDowngrade.question",
            questionFallback: "Can I cancel my plan anytime?",
            answers: [
                {
                    answerKey: "planDowngrade.answer",
                    answerFallback:
                        "Yes, you can cancel your subscription at any time. Once you cancel, your plan will remain active until the end of your current billing period, and you won’t be charged again after that.",
                },
            ],
        },
    ];

    const faqItems:FAQItem[] = isPricingPlanAvailable("enterprise") ? faqItemsDefault : faqItemsWithoutEnterprise;

    return (
        <section className="relative w-full  flex flex-col items-center justify-center overflow-hidden sm:py-20 py-10 bg-[#191919]">
            {/* Main Content Container */}
            <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 h-full flex flex-col justify-center">
                {/* Title Section */}
                <div className="text-center">
                    <h2 className="text-[#767EF6] sm:mb-4 md:mb-6 text-2xl md:text-3xl lg:text-[40px] !leading-8 md:!leading-10 lg:!leading-[55px] text-center mx-auto"
                        style={{
                            fontStyle: "normal",
                            fontWeight: 800,
                        }}
                    >
                        {getTranslation("title", "FAQ")}
                    </h2>
                </div>


                {/* FAQ */}
                <div className="space-y-4 md:space-y-6 mt-12">
                    {cmsFaqs.length
                        ? cmsFaqs.map((item, index) => (
                            <FaqAccordionItem
                                key={`cms-pricing-faq-${index}`}
                                id={`pricing-cms-faq-${index}`}
                                isOpen={openFaqKey === `cms-${index}`}
                                onToggle={() => toggleFaq(`cms-${index}`)}
                                question={item.question}
                            >
                                <p className="text-white font-semibold md:text-base lg:text-lg text-sm text-left leading-relaxed">
                                    {item.answer}
                                </p>
                            </FaqAccordionItem>
                        ))
                        : null}
                    {!cmsFaqs.length &&
                        faqItems.map((item, index) => (
                            <FaqAccordionItem
                                key={item.key}
                                id={`pricing-faq-${index}`}
                                isOpen={openFaqKey === item.key}
                                onToggle={() => toggleFaq(item.key)}
                                question={getTranslation(item.questionKey, item.questionFallback)}
                            >
                                <div className='text-white font-semibold md:text-base lg:text-lg text-sm text-left leading-relaxed space-y-4 sm:space-y-6 md:space-y-8'>
                                    {
                                        item.answers.map((answer, index) => {
                                            return (
                                                <p key={index}>
                                                    {getTranslation(answer.answerKey, answer.answerFallback)}
                                                </p>
                                            )
                                        })
                                    }
                                </div>
                            </FaqAccordionItem>
                        ))}


                </div>

            </div>
        </section>
    )
}

export default PricingFAQ